import * as React from 'react'

import {
  Block,
  getBlockUpdateOverride,
  getValueBasedOnSelectedVariants,
  type ColorBlock,
} from '@basehub/data/replicache/index'

import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { useSyncedValue } from '~/hooks/use-synced-value'
import { useBlockInputBaseProps } from '../block-wrapper'
import { parseColorValue, hexPattern, hexToRgb, rgbToHsl } from '~/lib/color'
import { toast } from 'sonner'
import { useEditorStore } from '../../editor-store'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { Button } from '~/sotano-kit/primitives/button'
import { useSelectedVariants } from '~/hooks/use-selected-variants'
import { useRepoUndoManager } from '~/replicache/frontend/undo-redo/manager'
import { UpdateBlockParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/crud'
import { useTabsStore } from '../../tabs/store'
import { useEditorMode } from '../../editor-mode-provider'

export const ColorBlockInput = ({
  block,
  parent,
  path,
  readOnly,
}: {
  block: ColorBlock
  parent: Block
  path: string
  readOnly?: boolean
}) => {
  const { data } = useRepoReplicache()
  const replicache = data?.replicache
  const editorMode = useEditorMode()
  const isSelected = useEditorStore(
    (s) => s.selectedBlockId === idToSourceBlockId(block)
  )
  const undoManager = useRepoUndoManager()
  const activeTabKey = useTabsStore((s) => s.activeTab?.key)

  const selectedDashboardVariants = useSelectedVariants({
    parentBlock: parent,
    block,
  })

  const _value: string = React.useMemo(
    () =>
      getValueBasedOnSelectedVariants({
        block,
        variantsToFilterBy: selectedDashboardVariants,
      }) ?? '',
    [block, selectedDashboardVariants]
  )

  const { onChange, ref, value } = useSyncedValue<HTMLInputElement>({
    value: _value,
    customEventsKey: `synced-value:color:${idToSourceBlockId(block)}`,
    setValue: React.useCallback(
      async (value, { internalValueRef, customEventsKey }) => {
        if (!replicache?.mutate) return
        const hexMatches = value.match(hexPattern)
        if (!hexMatches?.length) {
          return toast.error('Unable to parse this color')
        }

        const getParams = (v: string): UpdateBlockParams => ({
          id: block.id,
          instanceChildTarget: block.instanceChildTarget,
          data: getBlockUpdateOverride({
            block,
            newValue: v,
            selectedVariants: selectedDashboardVariants,
          }),
          path,
          strategy: 'merge',
          isoNow: new Date().toISOString(),
        })
        const { undo } = await replicache.mutate.updateBlock(getParams(value))
        if (undo) {
          undoManager.current?.push({
            redo: [
              {
                op: 'focus',
                args: {
                  preferredTabKey: activeTabKey,
                  focus: {
                    type: 'block:value',
                    blockId: idToSourceBlockId({
                      id: block.id,
                      sourceBlockId: block.sourceBlockId,
                    }),
                  },
                },
              },
              { op: 'updateBlock', args: getParams(internalValueRef.current) },
              {
                op: 'customWindowEvent',
                args: { name: customEventsKey, detail: { action: 'sync' } },
              },
            ],
            undo: [
              {
                op: 'focus',
                args: {
                  preferredTabKey: activeTabKey,
                  focus: {
                    type: 'block:value',
                    blockId: idToSourceBlockId({
                      id: block.id,
                      sourceBlockId: block.sourceBlockId,
                    }),
                  },
                },
              },
              { op: 'blockBatchMutations', args: { mutations: undo } },
              {
                op: 'customWindowEvent',
                args: { name: customEventsKey, detail: { action: 'sync' } },
              },
            ],
          })
        }
      },
      [
        replicache?.mutate,
        block,
        selectedDashboardVariants,
        path,
        undoManager,
        activeTabKey,
      ]
    ),
    rateLimitMs: 1000,
  })
  const baseProps = useBlockInputBaseProps(block)
  const rgb = hexToRgb(value)
  const HSL = rgb ? rgbToHsl(...rgb) : []
  const [H, S, L] = HSL
  const hsl = `${H}deg ${S}% ${L}%`

  React.useEffect(() => {
    if (!isSelected) return
    const handlePasteNew = (e: ClipboardEvent) => {
      const newValue = e.clipboardData?.getData('text')
      if (!newValue) return
      const parsedNewValue = parseColorValue(newValue)
      if (!parsedNewValue) return

      if (newValue) onChange(parsedNewValue)
    }

    document.addEventListener('paste', handlePasteNew)

    return () => {
      document.removeEventListener('paste', handlePasteNew)
    }
  }, [isSelected, onChange])

  const isReadOnly = readOnly || editorMode === 'read'

  return (
    <>
      <Button
        unstyled
        focusStyled
        onClick={() => ref.current?.click()}
        data-color-block
        className="rounded-md cursor-default border focus:outline-none focus:border-dark-control-base text-dark-bright w-full placeholder:select-none border-dark-border-solid bg-dark-shade placeholder:text-dark-faint h-9 text-sm p-1 rounded-b-none border-b-transparent z-10 outline-offset-3"
      >
        <span
          style={{ backgroundColor: value }}
          className="w-full h-full rounded-sm block"
        />
      </Button>

      <input
        {...baseProps}
        ref={ref}
        disabled={isReadOnly}
        readOnly={isReadOnly}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        tabIndex={-1}
        className="w-0 h-0 p-0 m-0 invisible"
      />

      <div className="grid items-center grid-cols-[repeat(3,minmax(auto,1fr))] whitespace-nowrap px-2 py-1 text-xs border-b cursor-text border-x border-dark-border-solid rounded-b-md bg-dark-shade text-dark-muted">
        <span className="pr-2">{value}</span>
        <span className="mx-auto max-w-max px-2">
          rgb({(rgb ?? []).join(', ')})
        </span>
        <span className="ml-auto max-w-max relative pl-2">hsl({hsl})</span>
      </div>
    </>
  )
}
